import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import StartVesselModal from "../components/StartVesselModal";
import { Plus, Boat, Calendar, Scales, User, PencilSimple, Trash, X, Package, FloppyDisk, DownloadSimple, FileCsv, CheckCircle, Warning, Clock, Play, CurrencyDollar } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";

/* ---------- helpers ---------- */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function pad(n) { return n.toString().padStart(2, "0"); }
function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toDateInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function fmtDM(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}
function fmtDMM(d) { return `${MONTHS[d.getMonth()]} ${d.getDate()}`; }

function statusColor(status) {
    if (status === "completed") return "#16A34A";
    if (status === "delayed") return "#DC2626";
    return "#94A3B8";
}
function statusBg(status) {
    if (status === "completed") return "#ECFDF5";
    if (status === "delayed") return "#FEF2F2";
    return "#F8FAFC";
}
function estimateBerthDays(item) {
    const tons = Number(item.tonnage || 0);
    if (!tons) return 1.0;
    const product = String(item.product || "").toLowerCase();
    let rate = 30000;
    if (product.includes("coal")) rate = 21500;
    else if (product.includes("iron")) rate = 35000;
    return Math.max(0.5, Math.round((tons / rate) * 10) / 10);
}

/* ---------- page ---------- */
export default function Lineup() {
    const { user } = useAuth();
    const canEdit = user && (user.role === "admin" || user.role === "manager");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [monthIdx, setMonthIdx] = useState(today.getMonth());
    const [division, setDivision] = useState("ALL");

    const load = async () => {
        try {
            const { data } = await api.get("/lineup");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const filtered = useMemo(() => {
        const ms = new Date(year, monthIdx, 1).getTime();
        const me = new Date(year, monthIdx + 1, 1).getTime();
        const inMonth = (iso) => {
            if (!iso) return false;
            const t = new Date(iso).getTime();
            return !Number.isNaN(t) && t >= ms && t < me;
        };
        return items
            .filter((it) => inMonth(it.etb) || inMonth(it.start_datetime) || inMonth(it.laycan_start) || inMonth(it.laycan_end) || inMonth(it.eta))
            .filter((it) => division === "ALL" || (it.division || "GTRB") === division)
            .sort((a, b) => new Date(a.etb || a.start_datetime) - new Date(b.etb || b.start_datetime));
    }, [items, year, monthIdx, division]);

    const totals = useMemo(() => {
        const forecast = filtered.reduce((s, it) => s + Number(it.tonnage || 0), 0);
        const completed = filtered.filter((it) => (it.auto_status || it.status) === "completed").length;
        const delayed = filtered.filter((it) => (it.auto_status || it.status) === "delayed").length;
        const on_schedule = filtered.filter((it) => (it.auto_status || it.status) === "on_schedule").length;
        return { forecast, count: filtered.length, completed, delayed, on_schedule };
    }, [filtered]);

    const range = useMemo(() => {
        if (filtered.length === 0) {
            const start = new Date(year, monthIdx, 1).getTime();
            const end = new Date(year, monthIdx + 1, 7).getTime();
            return { min: start, max: end, span: end - start };
        }
        const starts = filtered.map((it) => new Date(it.laycan_start || it.eta || it.etb || it.start_datetime).getTime()).filter((n) => !Number.isNaN(n));
        const ends = filtered.map((it) => {
            const etbMs = new Date(it.etb || it.start_datetime).getTime();
            return etbMs + estimateBerthDays(it) * 86_400_000;
        }).filter((n) => !Number.isNaN(n));
        const min = Math.min(...starts);
        const max = Math.max(...ends) + 86_400_000 * 2;
        return { min, max, span: max - min };
    }, [filtered, year, monthIdx]);

    const ticks = useMemo(() => {
        const step = Math.max(86_400_000 * 5, range.span / 4);
        return [0, 1, 2, 3].map((i) => range.min + i * step).filter((t) => t <= range.max + 86_400_000);
    }, [range]);

    const remove = async (item) => {
        if (!window.confirm(`Remove "${item.vessel_name}" from the line-up?`)) return;
        try {
            await api.delete(`/lineup/${item.id}`);
            setItems((prev) => prev.filter((x) => x.id !== item.id));
            toast.success("Removed from line-up");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const canStart = user?.role === "supervisor";
    const canSetBudget = ["admin", "finance"].includes(user?.role);
    const [startingItem, setStartingItem] = useState(null);
    const [budgetItem, setBudgetItem] = useState(null);
    const startVessel = (item) => setStartingItem(item);

    /* snapshots */
    const [snapshots, setSnapshots] = useState([]);
    const [showSnapshots, setShowSnapshots] = useState(false);
    const [savingSnapshot, setSavingSnapshot] = useState(false);
    const loadSnapshots = async () => {
        try { const { data } = await api.get("/lineup/snapshots"); setSnapshots(Array.isArray(data) ? data : []); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { if (canEdit) loadSnapshots(); }, [canEdit]);

    const saveSnapshot = async () => {
        setSavingSnapshot(true);
        try {
            const { data } = await api.post("/lineup/snapshots", { year, month: monthIdx + 1, division: division === "ALL" ? "ALL" : division });
            toast.success(`Saved · ${data.label} (${data.vessels_count} vessels)`);
            loadSnapshots();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingSnapshot(false); }
    };

    const removeSnapshot = async (snap) => {
        if (!window.confirm(`Delete snapshot "${snap.label}"?`)) return;
        try { await api.delete(`/lineup/snapshots/${snap.id}`); setSnapshots((p) => p.filter((s) => s.id !== snap.id)); toast.success("Snapshot deleted"); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const downloadSnapshotJson = async (snap) => {
        try {
            const { data } = await api.get(`/lineup/snapshots/${snap.id}`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `berthing-${(snap.label || snap.id).replace(/\s+/g, "_")}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];
    const periodLabel = `${MONTHS_LONG[monthIdx]} ${year}`;

    return (
        <div className="min-h-screen bg-slate-100">
            <Header />
            <main className="max-w-[1700px] mx-auto px-4 md:px-6 py-6">
                {/* === REPORT CARD === */}
                <div className="bg-white shadow-sm border border-slate-200 rounded-sm overflow-hidden" data-testid="berthing-report">
                    {/* Top bar: title + period */}
                    <div className="px-6 py-5 bg-[var(--grindrod-navy)] text-white flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <div className="uppercase tracking-[0.18em] text-[10px] text-white/60 font-bold">Grindrod · Richards Bay Division</div>
                            <h1 className="font-display font-black text-2xl md:text-3xl mt-0.5" data-testid="berthing-title">Monthly Berthing Schedule</h1>
                        </div>
                        <div className="flex items-baseline gap-3">
                            <div className="uppercase tracking-[0.18em] text-[10px] text-white/60 font-bold">Period</div>
                            <div className="font-display font-bold text-xl tabular" data-testid="berthing-period">{periodLabel}</div>
                        </div>
                    </div>

                    {/* KPI strip */}
                    <div className="grid grid-cols-2 md:grid-cols-5 border-b border-slate-200" data-testid="berthing-kpis">
                        <KpiCell label="Forecast" value={`${totals.forecast.toLocaleString()} t`} testid="kpi-forecast" emphasis />
                        <KpiCell label="Vessels" value={totals.count} testid="kpi-vessels" emphasis />
                        <KpiCell label="Completed" value={totals.completed} dot="#16A34A" testid="kpi-completed" />
                        <KpiCell label="Delayed" value={totals.delayed} dot="#DC2626" testid="kpi-delayed" />
                        <KpiCell label="On schedule" value={totals.on_schedule} dot="#94A3B8" testid="kpi-on-schedule" />
                    </div>

                    {/* Toolbar */}
                    <div className="px-4 md:px-6 py-3 flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50">
                        <Toolbar label="Company">
                            <span className="text-[var(--grindrod-navy)] font-display font-bold text-sm" data-testid="filter-company">Richards Bay</span>
                        </Toolbar>
                        <Toolbar label="Division">
                            <select value={division} onChange={(e) => setDivision(e.target.value)} className="g-input !py-1 !px-2 text-sm w-28" data-testid="filter-division">
                                <option value="ALL">All</option>
                                <option value="GTRB">GTRB</option>
                                <option value="RBTG">RBTG</option>
                            </select>
                        </Toolbar>
                        <Toolbar label="Year">
                            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="g-input !py-1 !px-2 text-sm w-24" data-testid="filter-year">
                                {years.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </Toolbar>
                        <Toolbar label="Month">
                            <select value={monthIdx} onChange={(e) => setMonthIdx(parseInt(e.target.value))} className="g-input !py-1 !px-2 text-sm w-32" data-testid="filter-month">
                                {MONTHS_LONG.map((m, i) => <option key={m} value={i}>{m}</option>)}
                            </select>
                        </Toolbar>
                        <div className="flex-1" />
                        {canEdit && (
                            <div className="flex items-center gap-2" data-testid="snapshot-actions">
                                <button onClick={() => setShowSnapshots((v) => !v)} className="btn-outline !py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid="snapshot-toggle-btn">
                                    <FloppyDisk size={14} /> Snapshots ({snapshots.length})
                                </button>
                                <button onClick={saveSnapshot} disabled={savingSnapshot} className="btn-outline !py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid="snapshot-save-btn">
                                    <FloppyDisk size={14} /> {savingSnapshot ? "Saving…" : "Save snapshot"}
                                </button>
                                <button onClick={() => setEditing("new")} className="btn-primary !py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid="lineup-new-btn">
                                    <Plus size={14} /> Add vessel
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Schedule (single horizontally-scrollable block) */}
                    <div className="overflow-x-auto" data-testid="berthing-grid">
                        <div className="min-w-[1400px]">
                            <ScheduleAxis year={year} ticks={ticks} range={range} />
                            <div className="grid berthing-grid bg-slate-50 text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold py-2.5 border-y border-slate-200">
                                <div className="px-4">Vessel</div>
                                <div className="text-center">Div</div>
                                <div>Client</div>
                                <div>Product</div>
                                <div className="text-right pr-3">Forecast</div>
                                <div>Laycan&nbsp;Start</div>
                                <div>Laycan&nbsp;End</div>
                                <div>ETA</div>
                                <div>ETB</div>
                                <div className="text-center">Status</div>
                                <div className="px-3">Berthing window</div>
                            </div>

                            {loading ? (
                                <div className="py-10 text-center text-slate-500" data-testid="lineup-loading">Loading line-up…</div>
                            ) : filtered.length === 0 ? (
                                <div className="py-16 text-center" data-testid="lineup-empty">
                                    <Boat size={56} weight="duotone" className="mx-auto text-slate-300 mb-3" />
                                    <div className="text-slate-700 font-display font-bold text-lg">No vessels scheduled</div>
                                    <div className="text-slate-500 text-sm">{periodLabel} {division !== "ALL" ? `· ${division}` : ""}</div>
                                    {canEdit && <div className="mt-4"><button onClick={() => setEditing("new")} className="btn-primary inline-flex items-center gap-2" data-testid="lineup-empty-new-btn"><Plus size={14}/> Add vessel</button></div>}
                                </div>
                            ) : (
                                filtered.map((it, idx) => (
                                    <ScheduleRow
                                        key={it.id}
                                        item={it}
                                        range={range}
                                        zebra={idx % 2 === 1}
                                        onEdit={canEdit ? () => setEditing(it) : null}
                                        onRemove={canEdit ? () => remove(it) : null}
                                        onStart={canStart && !it.actual_start_datetime ? () => startVessel(it) : null}
                                        onSetBudget={canSetBudget && !it.actual_start_datetime ? () => setBudgetItem(it) : null}
                                    />
                                ))
                            )}

                            <ScheduleAxis year={year} ticks={ticks} range={range} bottom />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="px-6 py-5 border-t border-slate-200 bg-slate-50" data-testid="berthing-notes">
                        <div className="font-display font-bold text-[var(--grindrod-navy)] uppercase tracking-wide text-sm mb-2">Notes</div>
                        {filtered.filter((i) => i.notes).length === 0 ? (
                            <div className="text-xs text-slate-400 italic">No notes for {periodLabel}.</div>
                        ) : (
                            <ul className="space-y-1 text-xs text-slate-600">
                                {filtered.filter((i) => i.notes).map((i) => (
                                    <li key={i.id} className="flex gap-2">
                                        <span className="font-bold text-[var(--grindrod-navy)] uppercase">{i.vessel_name}</span>
                                        <span className="text-slate-300">·</span>
                                        <span>{i.notes}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Snapshots panel */}
                {canEdit && showSnapshots && (
                    <div className="mt-5 bg-white shadow-sm border border-slate-200 rounded-sm overflow-hidden" data-testid="snapshots-panel">
                        <div className="px-6 py-3 bg-[var(--grindrod-navy)] text-white flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FloppyDisk size={16} weight="fill" />
                                <span className="font-display font-bold uppercase tracking-wide text-sm">Saved Monthly Snapshots</span>
                            </div>
                            <span className="text-[11px] text-white/70 tabular">{snapshots.length} archived</span>
                        </div>
                        {snapshots.length === 0 ? (
                            <div className="p-6 text-sm text-slate-500" data-testid="snapshots-empty">No snapshots saved yet.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="g-table">
                                    <thead>
                                        <tr>
                                            <th>Period</th>
                                            <th>Division</th>
                                            <th className="text-right">Vessels</th>
                                            <th className="text-right">Forecast</th>
                                            <th>Saved</th>
                                            <th>By</th>
                                            <th className="text-right pr-4">Backup</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {snapshots.map((s) => (
                                            <tr key={s.id} data-testid={`snapshot-${s.id}`}>
                                                <td className="font-semibold text-[var(--grindrod-navy)]">{s.label}</td>
                                                <td><DivisionBadge value={s.division} /></td>
                                                <td className="tabular text-right">{s.vessels_count}</td>
                                                <td className="tabular text-right">{Number(s.forecast_total || 0).toLocaleString()} t</td>
                                                <td className="text-xs text-slate-500 tabular">{new Date(s.saved_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                                                <td className="text-xs text-slate-500">{s.saved_by_name}</td>
                                                <td className="text-right pr-4">
                                                    <div className="inline-flex gap-1.5 justify-end">
                                                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/lineup/snapshots/${s.id}/csv`} target="_blank" rel="noopener noreferrer" className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1" data-testid={`snapshot-csv-${s.id}`}>
                                                            <FileCsv size={12} /> CSV
                                                        </a>
                                                        <button onClick={() => downloadSnapshotJson(s)} className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1" data-testid={`snapshot-json-${s.id}`}>
                                                            <DownloadSimple size={12} /> JSON
                                                        </button>
                                                        {user?.role === "admin" && (
                                                            <button onClick={() => removeSnapshot(s)} className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1 !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]" data-testid={`snapshot-delete-${s.id}`}>
                                                                <Trash size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {editing && (
                <LineupEditModal
                    item={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
            {startingItem && (
                <StartVesselModal
                    item={startingItem}
                    onClose={() => setStartingItem(null)}
                    onStarted={() => { setStartingItem(null); load(); }}
                />
            )}
            {budgetItem && (
                <LineupBudgetModal
                    item={budgetItem}
                    onClose={() => setBudgetItem(null)}
                    onSaved={() => { setBudgetItem(null); load(); }}
                />
            )}

            <style>{`
                .berthing-grid {
                    grid-template-columns:
                        minmax(180px, 1.4fr)  /* vessel */
                        70px                  /* div */
                        minmax(120px, 1fr)    /* client */
                        minmax(140px, 1fr)    /* product */
                        90px                  /* forecast */
                        80px 80px 80px 80px   /* dates */
                        110px                 /* status */
                        minmax(360px, 2fr);   /* gantt */
                    column-gap: 10px;
                    align-items: center;
                }
            `}</style>
        </div>
    );
}

/* ---------- subcomponents ---------- */
function KpiCell({ label, value, dot, testid, emphasis }) {
    return (
        <div className="px-4 md:px-6 py-3 border-r border-slate-200 last:border-r-0" data-testid={testid}>
            <div className="flex items-center gap-1.5 uppercase-label text-slate-500">
                {dot && <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: dot }} />}
                {label}
            </div>
            <div className={`tabular mt-0.5 ${emphasis ? "font-display font-black text-2xl text-[var(--grindrod-navy)]" : "font-display font-bold text-xl text-slate-700"}`}>
                {value}
            </div>
        </div>
    );
}

function Toolbar({ label, children }) {
    return (
        <div className="flex items-center gap-2">
            <span className="uppercase-label text-slate-500">{label}</span>
            <div>{children}</div>
        </div>
    );
}

function DivisionBadge({ value }) {
    const v = String(value || "GTRB").toUpperCase();
    const bg = v === "RBTG" ? "#7C2D12" : v === "GTRB" ? "#0B1B3D" : "#475569";
    return <span className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold tabular tracking-wide" style={{ background: bg, color: "white" }}>{v}</span>;
}

function StatusPill({ status, lateLabel }) {
    const map = {
        completed: { bg: "#ECFDF5", fg: "#15803D", border: "#86EFAC", icon: <CheckCircle size={10} weight="fill" />, label: "Completed" },
        delayed: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FCA5A5", icon: <Warning size={10} weight="fill" />, label: "Delayed" },
        on_schedule: { bg: "#F1F5F9", fg: "#475569", border: "#CBD5E1", icon: <Clock size={10} weight="fill" />, label: "On schedule" },
    };
    const cfg = map[status] || map.on_schedule;
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide border"
            style={{ background: cfg.bg, color: cfg.fg, borderColor: cfg.border }}
        >
            {cfg.icon}
            {cfg.label}
            {lateLabel && status === "delayed" && (
                <span className="ml-1 tabular font-semibold">· {lateLabel}</span>
            )}
        </span>
    );
}

function ScheduleAxis({ year, ticks, range, bottom }) {
    return (
        <div className={`grid berthing-grid bg-white py-2 border-${bottom ? "t" : "b"} border-slate-200`} data-testid={`schedule-axis${bottom ? "-bottom" : "-top"}`}>
            <div className="col-span-11 px-4 flex items-center justify-end pr-3">
                {!bottom && <span className="uppercase-label text-slate-500">ETB Timeline · {year}</span>}
                {bottom && <span className="uppercase-label text-slate-500">ETB Timeline · {year}</span>}
            </div>
            <div className="relative h-7 px-3">
                {ticks.map((t) => {
                    const pct = ((t - range.min) / range.span) * 100;
                    if (pct < 0 || pct > 100) return null;
                    return (
                        <div key={t} className="absolute top-0 h-full text-[10px] text-slate-500 tabular border-l border-dashed border-slate-200" style={{ left: `${pct}%` }}>
                            <span className="pl-1">{fmtDMM(new Date(t))}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ScheduleRow({ item, range, zebra, onEdit, onRemove, onStart, onSetBudget }) {
    const etbMs = new Date(item.etb || item.start_datetime).getTime();
    const days = estimateBerthDays(item);
    const widthMs = Math.max(86_400_000, days * 86_400_000);
    const leftPct = ((etbMs - range.min) / range.span) * 100;
    const widthPct = (widthMs / range.span) * 100;
    const status = item.auto_status || item.status || "on_schedule";
    const color = statusColor(status);
    const rowBg = zebra ? "#F8FAFC" : "#FFFFFF";
    const division = item.division || "GTRB";
    const divisionColor = division === "RBTG" ? "#7C2D12" : "#0B1B3D";
    const delayMin = Number(item.delay_minutes || 0);
    const lateLabel = delayMin > 0 ? `+${fmtDuration(delayMin)} late` : null;

    return (
        <div
            className="grid berthing-grid py-3 border-b border-slate-100 hover:bg-slate-50 group text-[12.5px] relative"
            style={{ background: rowBg, borderLeft: `3px solid ${divisionColor}` }}
            data-testid={`schedule-row-${item.id}`}
        >
            <div className="px-4 truncate">
                <div className="font-display font-bold text-[var(--grindrod-navy)] uppercase tracking-wide truncate" title={item.vessel_name}>{item.vessel_name}</div>
                {item.actual_start_datetime && (
                    <div className="text-[10px] text-slate-500 tabular">Started {fmtDM(item.actual_start_datetime)}</div>
                )}
                {onStart && (
                    <button
                        onClick={onStart}
                        className="sm:hidden mt-1.5 text-[11px] px-2 py-1 bg-[var(--grindrod-green)] text-white rounded-sm inline-flex items-center gap-1 shadow-sm font-bold uppercase tracking-wide"
                        data-testid={`lineup-start-mobile-${item.id}`}
                    >
                        <Play size={11} weight="fill" /> Start vessel
                    </button>
                )}
            </div>
            <div className="text-center"><DivisionBadge value={division} /></div>
            <div className="text-slate-700 truncate" title={item.client_name}>{item.client_name || "—"}</div>
            <div className="text-slate-700 truncate" title={item.product || ""}>{item.product || "—"}</div>
            <div className="text-right tabular text-slate-800 pr-3 font-semibold">{Number(item.tonnage || 0).toLocaleString()}</div>
            <div className="tabular text-slate-600">{fmtDM(item.laycan_start)}</div>
            <div className="tabular text-slate-600">{fmtDM(item.laycan_end)}</div>
            <div className="tabular text-slate-600">{fmtDM(item.eta)}</div>
            <div className="tabular text-slate-800 font-semibold">{fmtDM(item.etb || item.start_datetime)}</div>
            <div className="text-center"><StatusPill status={status} lateLabel={lateLabel} /></div>
            <div className="relative h-7 px-3">
                <div className="absolute inset-y-0 left-3 right-3 border-l border-dashed border-slate-200" />
                <div
                    className="absolute top-1 h-5 rounded-[2px] flex items-center"
                    style={{
                        left: `calc(12px + ${Math.max(leftPct, 0)}%)`,
                        width: `${Math.max(Math.min(widthPct, 100 - Math.max(leftPct, 0)), 1.5)}%`,
                        background: color,
                        boxShadow: status === "delayed" ? "0 0 0 1px #FCA5A5" : "none",
                    }}
                    data-testid={`gantt-bar-${item.id}`}
                    title={`${item.vessel_name} · ${days.toFixed(1)} days · ${status.replace("_", " ")}`}
                />
                <div
                    className="absolute top-1 text-[11px] tabular text-slate-700 leading-5 pl-1.5 whitespace-nowrap"
                    style={{ left: `calc(12px + ${Math.min(Math.max(leftPct, 0) + Math.max(widthPct, 1.5), 100)}%)` }}
                >
                    {days.toFixed(1)} d
                </div>
            </div>
            {(onEdit || onRemove || onStart || onSetBudget) && (
                <div className="absolute right-3 top-1.5 flex gap-1.5 z-10 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-testid={`schedule-actions-${item.id}`}>
                    {onStart && (
                        <button onClick={onStart} className="text-[11px] px-2 py-0.5 bg-[var(--grindrod-green)] text-white rounded-sm inline-flex items-center gap-1 shadow-sm font-semibold hover:opacity-90" data-testid={`lineup-start-${item.id}`}>
                            <Play size={11} weight="fill" /> Start
                        </button>
                    )}
                    {onSetBudget && (
                        <button onClick={onSetBudget} className="text-[10px] px-1.5 py-0.5 bg-white border border-[#B36B00] text-[#B36B00] rounded-sm hover:bg-amber-50 inline-flex items-center gap-1 shadow-sm" data-testid={`lineup-budget-${item.id}`} title={item.budget_zar ? `Budget: R ${Number(item.budget_zar).toLocaleString()}` : "Set vessel budget"}>
                            <CurrencyDollar size={10} weight="bold" /> {item.budget_zar ? "Budget" : "Set budget"}
                        </button>
                    )}
                    {onEdit && (
                        <button onClick={onEdit} className="text-[10px] px-1.5 py-0.5 bg-white border border-slate-300 rounded-sm hover:bg-slate-100 inline-flex items-center gap-1 shadow-sm" data-testid={`lineup-edit-${item.id}`}>
                            <PencilSimple size={10} /> Edit
                        </button>
                    )}
                    {onRemove && (
                        <button onClick={onRemove} className="text-[10px] px-1.5 py-0.5 bg-white border border-[var(--grindrod-red)] text-[var(--grindrod-red)] rounded-sm hover:bg-red-50 inline-flex items-center gap-1 shadow-sm" data-testid={`lineup-delete-${item.id}`}>
                            <Trash size={10} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/* ---------- modal (unchanged content, kept compact) ---------- */
function LineupEditModal({ item, onClose, onSaved }) {
    const isNew = !item;
    const [clientName, setClientName] = useState(item?.client_name || "");
    const [vesselName, setVesselName] = useState(item?.vessel_name || "");
    const [tonnage, setTonnage] = useState(item?.tonnage?.toString() || "");
    const [product, setProduct] = useState(item?.product || "");
    const [laycanStart, setLaycanStart] = useState(toDateInput(item?.laycan_start) || "");
    const [laycanEnd, setLaycanEnd] = useState(toDateInput(item?.laycan_end) || "");
    const [eta, setEta] = useState(item?.eta ? toLocalInput(item.eta) : "");
    const [etb, setEtb] = useState(item?.etb ? toLocalInput(item.etb) : (item ? toLocalInput(item.start_datetime) : toLocalInput(new Date(Date.now() + 86_400_000).toISOString())));
    const [status, setStatus] = useState(item?.status || "on_schedule");
    const [divisionVal, setDivisionVal] = useState(item?.division || "GTRB");
    const [notes, setNotes] = useState(item?.notes || "");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!etb) { toast.error("Planned start (ETB) is required"); return; }
        setLoading(true);
        const payload = {
            client_name: clientName.trim(),
            vessel_name: vesselName.trim(),
            tonnage: parseFloat(tonnage) || 0,
            start_datetime: new Date(etb).toISOString(),
            etb: new Date(etb).toISOString(),
            eta: eta ? new Date(eta).toISOString() : null,
            laycan_start: laycanStart ? new Date(laycanStart).toISOString() : null,
            laycan_end: laycanEnd ? new Date(laycanEnd).toISOString() : null,
            product: product.trim(),
            status,
            division: divisionVal,
            notes,
        };
        try {
            if (isNew) { await api.post("/lineup", payload); toast.success("Added to schedule"); }
            else { await api.patch(`/lineup/${item.id}`, payload); toast.success("Schedule updated"); }
            onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="lineup-modal">
            <div className="g-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="g-card-header-red">
                    <Boat size={18} weight="fill" />
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-white/70">Berthing schedule</div>
                        <div className="font-display font-bold text-white">{isNew ? "Add upcoming vessel" : `Edit ${item.vessel_name}`}</div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="lineup-modal-close"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-5" data-testid="lineup-form">
                    <div>
                        <div className="uppercase-label text-[var(--grindrod-red)] mb-2">Required · Planner inputs</div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="uppercase-label block mb-1.5"><User size={12} className="inline mr-1" /> Client name <span className="text-[var(--grindrod-red)]">*</span></label>
                                <input value={clientName} onChange={(e) => setClientName(e.target.value)} className="g-input" required placeholder="e.g. TCM" data-testid="lineup-client" />
                            </div>
                            <div>
                                <label className="uppercase-label block mb-1.5"><Boat size={12} className="inline mr-1" /> Vessel name <span className="text-[var(--grindrod-red)]">*</span></label>
                                <input value={vesselName} onChange={(e) => setVesselName(e.target.value)} className="g-input" required placeholder="e.g. PERGAMOS" data-testid="lineup-vessel" />
                            </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4 mt-4">
                            <div>
                                <label className="uppercase-label block mb-1.5"><Package size={12} className="inline mr-1" /> Product type <span className="text-[var(--grindrod-red)]">*</span></label>
                                <input value={product} onChange={(e) => setProduct(e.target.value)} className="g-input" required placeholder="e.g. Magnetite Iron Ore" data-testid="lineup-product" />
                            </div>
                            <div>
                                <label className="uppercase-label block mb-1.5">Division <span className="text-[var(--grindrod-red)]">*</span></label>
                                <select value={divisionVal} onChange={(e) => setDivisionVal(e.target.value)} className="g-input" required data-testid="lineup-division">
                                    <option value="GTRB">GTRB</option>
                                    <option value="RBTG">RBTG</option>
                                </select>
                            </div>
                            <div>
                                <label className="uppercase-label block mb-1.5"><Calendar size={12} className="inline mr-1" /> Planned start <span className="text-[var(--grindrod-red)]">*</span></label>
                                <input type="datetime-local" value={etb} onChange={(e) => setEtb(e.target.value)} className="g-input tabular" required data-testid="lineup-etb" />
                            </div>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-2">If the vessel doesn't start by the planned date, the system will auto-flag it as <span className="font-semibold text-[var(--grindrod-red)]">Delayed</span>.</p>
                    </div>

                    <details className="border border-slate-200 rounded-sm" open>
                        <summary className="cursor-pointer px-3 py-2 uppercase-label text-slate-600 select-none">Additional details (optional)</summary>
                        <div className="p-4 space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="uppercase-label block mb-1.5"><Scales size={12} className="inline mr-1" /> Forecast (tons)</label>
                                    <input type="number" min="0" step="1" value={tonnage} onChange={(e) => setTonnage(e.target.value)} className="g-input tabular" placeholder="90000" data-testid="lineup-tonnage" />
                                </div>
                                <div>
                                    <label className="uppercase-label block mb-1.5">Status (override)</label>
                                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="g-input" data-testid="lineup-status">
                                        <option value="on_schedule">On Schedule</option>
                                        <option value="delayed">Delayed</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                    <label className="uppercase-label block mb-1.5"><Calendar size={12} className="inline mr-1" /> Laycan start</label>
                                    <input type="date" value={laycanStart} onChange={(e) => setLaycanStart(e.target.value)} className="g-input tabular" data-testid="lineup-laycan-start" />
                                </div>
                                <div>
                                    <label className="uppercase-label block mb-1.5"><Calendar size={12} className="inline mr-1" /> Laycan end</label>
                                    <input type="date" value={laycanEnd} onChange={(e) => setLaycanEnd(e.target.value)} className="g-input tabular" data-testid="lineup-laycan-end" />
                                </div>
                                <div>
                                    <label className="uppercase-label block mb-1.5"><Calendar size={12} className="inline mr-1" /> ETA</label>
                                    <input type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} className="g-input tabular" data-testid="lineup-eta" />
                                </div>
                            </div>
                            <div>
                                <label className="uppercase-label block mb-1.5">Notes</label>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="g-input" placeholder="e.g. Pilot booked, tug on standby" data-testid="lineup-notes" />
                            </div>
                        </div>
                    </details>

                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="lineup-cancel">Cancel</button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="lineup-save">
                            {loading ? "Saving…" : isNew ? "Add to schedule" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


/* ---------- Lineup budget modal — pre-vessel-start budget (Finance/Admin) ---------- */
const LINEUP_COST_CATS = ["fuel","breakdowns","manpower","claims","marine","plant_hire","maintenance","other"];

function LineupBudgetModal({ item, onClose, onSaved }) {
    const [total, setTotal] = useState(item.budget_zar || 0);
    const [notes, setNotes] = useState(item.budget_notes || "");
    const [breakdown, setBreakdown] = useState(item.budget_breakdown || {});
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/lineup/${item.id}/budget`);
                if (data) {
                    setTotal(data.total_budget_zar || 0);
                    setNotes(data.notes || "");
                    setBreakdown(data.breakdown || {});
                }
            } catch (err) {
                console.warn("Lineup budget load failed:", err?.message || err);
            }
            setLoaded(true);
        })();
    }, [item.id]);

    const setCat = (k, v) => setBreakdown({ ...breakdown, [k]: parseFloat(v) || 0 });
    const submit = async (e) => {
        e?.preventDefault();
        setSaving(true);
        try {
            await api.post(`/lineup/${item.id}/budget`, {
                total_budget_zar: parseFloat(total) || 0,
                notes: notes || null,
                breakdown: Object.fromEntries(Object.entries(breakdown).filter(([, v]) => v > 0)),
            });
            toast.success("Budget saved");
            onSaved();
        } catch (e2) {
            toast.error(formatApiError(e2));
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-3" onClick={onClose} data-testid="lineup-budget-modal">
            <form onSubmit={submit} className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--grindrod-navy)] text-white px-4 py-2 flex items-center gap-2">
                    <CurrencyDollar size={16} weight="fill" className="text-amber-300" />
                    <div className="font-display font-black uppercase tracking-wide text-sm flex-1">Budget · {item.vessel_name}</div>
                    <button type="button" onClick={onClose} className="text-white"><X size={14} /></button>
                </div>
                <div className="p-4 space-y-3 text-sm">
                    <div className="text-xs text-slate-500">{item.client_name} · {item.product} · {Number(item.tonnage || 0).toLocaleString()} t · ETB {fmtDM(item.etb || item.start_datetime)}</div>
                    <div>
                        <label className="uppercase-label block mb-1">Total budget (ZAR) *</label>
                        <input type="number" min="0" step="0.01" className="g-input tabular text-lg font-display font-bold" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="lineup-budget-total" />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Optional breakdown by category</div>
                    <div className="grid grid-cols-2 gap-2">
                        {LINEUP_COST_CATS.map((c) => (
                            <div key={c}>
                                <label className="uppercase-label block mb-1">{c.replace("_"," ")}</label>
                                <input type="number" min="0" step="0.01" className="g-input tabular" value={breakdown[c] || 0} onChange={(e) => setCat(c, e.target.value)} />
                            </div>
                        ))}
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1">Notes</label>
                        <input className="g-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Assumptions, contingencies…" />
                    </div>
                    {item.budget_set_by && (
                        <div className="text-[10.5px] text-slate-400 italic">
                            Last set by {item.budget_set_by}{item.budget_set_at ? ` on ${new Date(item.budget_set_at).toLocaleDateString()}` : ""}.
                            Budget copies onto the vessel automatically when supervisor starts the line-up.
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
                        <button type="submit" className="btn-primary" disabled={!loaded || saving} data-testid="lineup-budget-save">{saving ? "Saving…" : "Save budget"}</button>
                    </div>
                </div>
            </form>
        </div>
    );
}
